import { memo, useEffect, useCallback } from "react";
import { Phone, PhoneOff, Video } from "lucide-react";
import { useRingtone } from "@/hooks/useRingtone";

interface IncomingCallModalProps {
  callerName: string;
  callerPhoto: string | null;
  onAnswer: () => void;
  onDecline: () => void;
}

const IncomingCallModal = memo(({
  callerName,
  callerPhoto,
  onAnswer,
  onDecline,
}: IncomingCallModalProps) => {
  const { startRingtone, stopRingtone } = useRingtone();

  // Start ringtone when modal appears
  useEffect(() => {
    startRingtone();
    
    return () => {
      stopRingtone();
    };
  }, [startRingtone, stopRingtone]);

  const handleAnswer = useCallback(() => {
    stopRingtone();
    onAnswer();
  }, [stopRingtone, onAnswer]);

  const handleDecline = useCallback(() => {
    stopRingtone();
    onDecline();
  }, [stopRingtone, onDecline]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col items-center justify-center animate-fade-in">
      {/* Caller info */}
      <div className="relative mb-8">
        <div className="w-32 h-32 rounded-full overflow-hidden bg-muted ring-4 ring-pop animate-pulse">
          {callerPhoto ? (
            <img src={callerPhoto} alt={callerName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-muted-foreground">
              {callerName?.[0]}
            </div>
          )}
        </div>
        <div className="absolute -top-2 -right-2 p-2 rounded-full bg-pop text-white animate-ping-slow">
          <Video className="w-5 h-5" />
        </div>
      </div>

      <h2 className="text-white text-2xl font-semibold mb-2">{callerName}</h2>
      <p className="text-white/70 mb-12 animate-pulse">Incoming video call...</p>

      {/* Action buttons */}
      <div className="flex gap-8">
        <button
          onClick={handleDecline}
          className="flex flex-col items-center gap-2"
        >
          <div className="p-5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors">
            <PhoneOff className="w-8 h-8" />
          </div>
          <span className="text-white/70 text-sm">Decline</span>
        </button>

        <button
          onClick={handleAnswer}
          className="flex flex-col items-center gap-2"
        >
          <div className="p-5 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors animate-bounce">
            <Phone className="w-8 h-8" />
          </div>
          <span className="text-white/70 text-sm">Answer</span>
        </button>
      </div>
    </div>
  );
});

IncomingCallModal.displayName = "IncomingCallModal";

export default IncomingCallModal;
